import { eng } from "@/lib/engineApi";
import { useGrid } from "@/state/grid";

// Inspections overview — faithful React port of vOverview().
export default function OverviewView() {
  const version = useGrid((s) => s.version);
  const setView = useGrid((s) => s.setView);
  void version;

  const e = eng();
  const C = e.C;
  const D = e.D;
  const qc = e.qcCounts();
  const all = e.allResults();
  const cats = [...new Set(D.itp.map((it: any) => it.cat))] as string[];
  const ftp = qc.done ? Math.round((qc.pass / qc.done) * 100) : 0;
  const open = Math.max(0, qc.total - qc.done);
  const rc = e.rfiCounts();
  const pendingInspections = rc.pending + rc.scheduled;
  const fs = e.fieldStats();
  const siteOverall = {
    totalTables: fs.reduce((a: number, f: any) => a + f.tables, 0),
    totalAccepted: fs.reduce((a: number, f: any) => a + f.tablesAccepted, 0),
    totalRejected: fs.reduce((a: number, f: any) => a + f.tablesRejected, 0),
  };

  const kpis: [string, string | number, string, string][] = [
    ["QC acceptance", qc.rate + "%", `${qc.done} of ${qc.total} checks closed`, C.grass],
    ["First-time pass", ftp + "%", `${qc.pass} passed without rework`, C.steel],
    ["Failed checks", qc.fail, "raised as non-conformance", C.rust],
    ["Pending inspections", pendingInspections, `${rc.pending} new · ${rc.scheduled} scheduled`, C.amber],
    ["Open NCRs", qc.ncr, "awaiting close-out", C.hiviz],
    ["Tables inspected", qc.tables, `across ${cats.length} categories`, C.grass],
  ];

  const catData = cats.map((cat) => {
    const code = D.itp.find((it: any) => it.cat === cat)!.code;
    const rs = all.filter((r: any) => r.cat === cat);
    return {
      cat,
      code,
      pass: rs.filter((r: any) => r.status === "pass").length,
      fail: rs.filter((r: any) => r.status === "fail").length,
      total: rs.length,
    };
  });
  const maxTot = Math.max(1, ...catData.map((d) => d.total));
  const w = (v: number) => (maxTot ? (v / maxTot) * 100 : 0);

  // donut
  const segs: [string, number, string][] = [
    ["Pass", qc.pass, C.grass],
    ["Fail", qc.fail, C.rust],
    ["Open", open, C.dim2],
  ];
  let acc = 0;
  const circ = 2 * Math.PI * 44;
  const donut = segs.map(([l, v, col]) => {
    const frac = qc.total ? v / qc.total : 0;
    const dash = frac * circ;
    const node = (
      <circle
        key={l}
        r="44"
        cx="62"
        cy="62"
        fill="none"
        stroke={col}
        strokeWidth="15"
        strokeDasharray={`${dash} ${circ - dash}`}
        strokeDashoffset={-acc}
        transform="rotate(-90 62 62)"
        strokeLinecap="butt"
      />
    );
    acc += dash;
    return node;
  });

  const ncrs = all.filter((r: any) => r.ncr);
  // Issue #8: punch items group by location (Field → Table), matching the punch list.
  const punchByLoc = e.punchByLocation();
  const pLocs = Object.keys(punchByLoc);
  const pc = e.punchCounts();

  const jumpField = (fid: string) => {
    e.state.selField = fid;
    e.state.selTable = null;
    setView("sitemap");
  };

  return (
    <div className="ovwrap">
      <div className="vhead">
        <h1 className="serif">Inspections overview</h1>
        <div className="vs">
          {D.project.name} · {D.project.location}
        </div>
        <div className="vtag">ITP-BAL · Rev B</div>
      </div>

      <div className="kpis">
        {kpis.map(([k, v, t, col]) => (
          <div className="kpi" style={{ ["--accent" as any]: col }} key={k}>
            <div className="kk">{k}</div>
            <div className="kv" style={{ color: col }}>
              {v}
            </div>
            <div className="kt">{t}</div>
          </div>
        ))}
      </div>

      <div className="ovgrid">
        <div className="card">
          <h3>Result by category</h3>
          <div className="csub">Pass / fail across all recorded inspection checks</div>
          {catData.map((d) => (
            <div className="fbar-row" style={{ gridTemplateColumns: "160px 1fr 56px" }} key={d.cat}>
              <div
                className="fl"
                title={d.cat}
                style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {d.cat}
              </div>
              <div className="fbar-track">
                <div className="fbar-seg" style={{ width: `${w(d.pass)}%`, background: C.grass }} />
                <div className="fbar-seg" style={{ width: `${w(d.fail)}%`, background: C.rust }} />
              </div>
              <div className="fbar-val">
                <span className="mono">
                  {d.pass}/{d.total}
                </span>
              </div>
            </div>
          ))}
          <div className="typelegend" style={{ marginTop: 14 }}>
            <span>
              <i style={{ background: C.grass, borderRadius: 2 }} />
              Pass
            </span>
            <span>
              <i style={{ background: C.rust, borderRadius: 2 }} />
              Fail
            </span>
          </div>
        </div>

        <div className="card">
          <h3>Overall acceptance</h3>
          <div className="csub">Status distribution across all inspection items</div>
          <div className="donut-wrap">
            <svg width="124" height="124" viewBox="0 0 124 124">
              {donut}
              <text x="62" y="60" textAnchor="middle" fill={C.ink} fontSize="26" fontWeight="600" fontFamily="IBM Plex Sans">
                {qc.rate}%
              </text>
              <text x="62" y="77" textAnchor="middle" fill={C.dim2} fontSize="8.5" letterSpacing="1.5" fontFamily="IBM Plex Sans">
                CLOSED
              </text>
            </svg>
            <div className="dleg">
              <div>
                <i style={{ background: C.grass }} />
                Pass <b>{qc.pass}</b>
              </div>
              <div>
                <i style={{ background: C.rust }} />
                Fail / NCR <b>{qc.fail}</b>
              </div>
              <div>
                <i style={{ background: C.dim2 }} />
                Open <b>{open}</b>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 14 }}>
        <div className="punchcard-h">
          <div>
            <h3>By field</h3>
            <div className="csub">Per-field breakdown across all 13 fields. Click any field to jump to its site map.</div>
          </div>
          <div className="csub" style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 10 }}>
            <span style={{ color: C.grass }}>●</span> {siteOverall.totalAccepted} tables accepted ·{" "}
            <span style={{ color: C.rust }}>●</span> {siteOverall.totalRejected} rejected ·{" "}
            <span style={{ color: C.dim }}>●</span> {siteOverall.totalTables} planned
          </div>
        </div>
        <div className="fieldgrid">
          {fs.map((f: any) => {
            const greenPct = f.tables ? (f.tablesAccepted / f.tables) * 100 : 0;
            const redPct = f.tables ? (f.tablesRejected / f.tables) * 100 : 0;
            const isDormant = f.tablesTouched === 0 && f.tablesAccepted === 0 && f.tablesRejected === 0;
            return (
              <div
                className="fieldcard"
                key={f.id}
                title={`Open ${f.id} on the site map`}
                onClick={() => jumpField(f.id)}
                style={{ cursor: "pointer" }}
              >
                <div className="fc-head">
                  <div className="fc-name">{f.id}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div className="fc-meta">
                      {f.tables} tables · {f.mwp} MWp
                    </div>
                    <button
                      className="export-btn"
                      title={`Export ${f.id} acceptance report`}
                      style={{ fontSize: 10, padding: "3px 8px" }}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        e.exportFieldReport(f.id);
                      }}
                    >
                      ⬇ Report
                    </button>
                  </div>
                </div>
                <div className="fc-bar" title={`${f.tablesAccepted} accepted, ${f.tablesRejected} rejected of ${f.tables} planned`}>
                  <div className="fc-bar-seg" style={{ width: `${greenPct}%`, background: C.grass }} />
                  <div className="fc-bar-seg" style={{ width: `${redPct}%`, background: C.rust }} />
                </div>
                <div className="fc-stats">
                  <div className="fc-stat">
                    <span className="fc-sv" style={{ color: C.grass }}>
                      {f.pass}
                    </span>
                    <span className="fc-sl">pass</span>
                  </div>
                  <div className="fc-stat">
                    <span className="fc-sv" style={{ color: f.fail ? C.rust : C.dim2 }}>
                      {f.fail}
                    </span>
                    <span className="fc-sl">fail</span>
                  </div>
                  <div className="fc-stat">
                    <span className="fc-sv" style={{ color: f.ncr ? C.hiviz : C.dim2 }}>
                      {f.ncr}
                    </span>
                    <span className="fc-sl">NCR</span>
                  </div>
                  <div className="fc-stat">
                    <span className="fc-sv" style={{ color: isDormant ? C.dim2 : C.ink }}>
                      {f.tablesTouched}/{f.tables}
                    </span>
                    <span className="fc-sl">tables</span>
                  </div>
                </div>
                {isDormant ? <div className="fc-dormant">Not started</div> : null}
              </div>
            );
          })}
        </div>
      </div>

      <div className="card" style={{ marginTop: 14 }}>
        <h3>Non-conformance register (NCR)</h3>
        <div className="csub">Open items linked to failed inspections</div>
        <table className="bom">
          <thead>
            <tr>
              <th>NCR</th>
              <th>Location</th>
              <th>ITP Ref</th>
              <th>Inspection Item</th>
              <th>Category</th>
              <th>Raised by</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {ncrs.length ? (
              ncrs.map((r: any, i: number) => (
                <tr key={i}>
                  <td className="mono" style={{ color: C.rust }}>
                    {r.ncr}
                  </td>
                  <td className="mono">
                    {r.fid} · T{r.t}
                  </td>
                  <td className="mono">{r.itp}</td>
                  <td>{r.item}</td>
                  <td style={{ color: C.dim }}>{r.cat}</td>
                  <td>{r.by || "—"}</td>
                  <td>
                    <span className="pill" style={{ background: `${C.hiviz}22`, color: C.hiviz }}>
                      OPEN
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} style={{ color: C.dim, textAlign: "center", padding: 18 }}>
                  No open non-conformances.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="card punchcard" style={{ marginTop: 14 }}>
        <div className="punchcard-h">
          <div>
            <h3>Punch list</h3>
            <div className="csub">All rejected items requiring close-out, grouped by location</div>
          </div>
          <button className="export-btn go" onClick={() => setView("punch")}>
            Open punch list →
          </button>
        </div>
        <div className="punchcard-stats">
          <div className="pcs">
            <b style={{ color: C.rust }}>{pc.open}</b>
            <span>Open</span>
          </div>
          <div className="pcs">
            <b style={{ color: C.grass }}>{pc.resolved}</b>
            <span>Resolved</span>
          </div>
          <div className="pcs">
            <b>{pc.total}</b>
            <span>Total</span>
          </div>
        </div>
        {pLocs.length ? (
          <div className="punchcard-cats">
            {pLocs.map((c) => {
              const o = punchByLoc[c].filter((p: any) => !p.resolved).length;
              return (
                <span className="pcc" key={c}>
                  <i style={{ background: o ? C.rust : C.grass }} />
                  {c} <b>{o}</b>
                </span>
              );
            })}
          </div>
        ) : (
          <div className="csub" style={{ marginTop: 10 }}>
            No rejected items yet — nothing to close out.
          </div>
        )}
      </div>
    </div>
  );
}
